// Admin-only: which type-10 verifier(s) each patient is currently
// assigned to, with any day/block label from the Quick Assigner Notes
// workflow. Backed by GET /api/users/patientAssignments (admin-only,
// type 1/8 — the route itself only reads type-10 verifiers' `patients`
// arrays, deduped, so this never shows catch-all admin assignments).
//
// `PatientAssignmentsPanel` is the reusable content (toolbar + table) —
// used both by the standalone /patient-assignments page below and by the
// popup modal opened from the Assign Patients page's own header button.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import SideBar from "../../common/SideBar";
import { apiUrl } from "../../../config/config";

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

const pageWrap = {
  fontFamily: FONT,
  background: "#0b1220",
  minHeight: "100vh",
  // Match the global sidebar offset (.page-wrapper / .ubt-page) so content
  // doesn't render underneath the fixed sidebar rail.
  marginLeft: "44px",
  padding: "24px 28px",
  color: "#e2e8f0",
};
const headerRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "20px",
  flexWrap: "wrap",
  gap: "12px",
};
const h1 = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#f8fafc",
};
const toolbar = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "16px",
};
const inputStyle = {
  background: "#141a29",
  border: "1px solid #242833",
  color: "#e2e8f0",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  outline: "none",
};
const tableWrap = {
  background: "#101625",
  border: "1px solid #242833",
  borderRadius: "10px",
  overflow: "auto",
};
const th = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#8a93a0",
  borderBottom: "1px solid #242833",
  whiteSpace: "nowrap",
};
const td = {
  padding: "10px 14px",
  fontSize: "13px",
  color: "#e2e8f0",
  borderBottom: "1px solid #1b2130",
  verticalAlign: "top",
};
const pill = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 700,
  background: "rgba(90,200,250,0.13)",
  color: "#5ac8fa",
  border: "1px solid rgba(90,200,250,0.3)",
  marginRight: "4px",
  marginBottom: "4px",
};
const blockChip = {
  display: "inline-block",
  padding: "1px 6px",
  fontSize: "9px",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "#1a6fa3",
  background: "#e8f4fd",
  border: "1px solid #a9d6f5",
  borderRadius: "8px",
  marginLeft: "4px",
};

// Reusable content — no SideBar, no page padding — so it can be dropped
// straight into a modal as well as a full page.
export function PatientAssignmentsPanel({ onClose }) {
  const [patients, setPatients] = useState([]);
  const [verifiers, setVerifiers] = useState([]);
  const [search, setSearch] = useState("");
  // Composite "server:verifierId" — a verifier id is only meaningful on the
  // one server that issued it, so the dropdown value carries both.
  const [verifierKey, setVerifierKey] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = { limit: 200 };
    if (search.trim()) params.search = search.trim();
    if (verifierKey) {
      const sep = verifierKey.indexOf(":");
      params.server = verifierKey.slice(0, sep);
      params.verifierId = verifierKey.slice(sep + 1);
    }
    axios
      .get(apiUrl + "api/users/patientAssignments", { params })
      .then((res) => {
        const data = res.data || {};
        setPatients(Array.isArray(data.patients) ? data.patients : []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          (err.response && err.response.data && err.response.data.error) ||
            err.message ||
            "Failed to load assignments"
        );
        setLoading(false);
      });
  }, [search, verifierKey]);

  useEffect(() => {
    const t = setTimeout(load, 300); // debounce search typing
    return () => clearTimeout(t);
  }, [load]);

  // Verifier dropdown is loaded once, unfiltered, across every server — so
  // picking a server-specific verifier never shrinks the list other servers'
  // verifiers disappear from it.
  useEffect(() => {
    axios
      .get(apiUrl + "api/users/patientAssignments", { params: { limit: 1 } })
      .then((res) => {
        const data = res.data || {};
        setVerifiers(Array.isArray(data.verifiers) ? data.verifiers : []);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <div style={headerRow}>
        <h1 style={h1}>Patient Assignments</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ fontSize: 12, color: "#8a93a0" }}>
            {loading ? "Loading…" : `${total} assigned patient${total === 1 ? "" : "s"}`}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "1px solid #242833",
                color: "#e2e8f0",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              ✕ Close
            </button>
          )}
        </div>
      </div>

      <div style={toolbar}>
        <input
          type="text"
          placeholder="Search patient, device id, customer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 260 }}
        />
        <select
          value={verifierKey}
          onChange={(e) => setVerifierKey(e.target.value)}
          style={inputStyle}
        >
          <option value="">All servers, all type-10 verifiers</option>
          {verifiers
            .slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map((v) => (
              <option key={`${v.server}:${v._id}`} value={`${v.server}:${v._id}`}>
                {v.name || v._id} ({v.server})
              </option>
            ))}
        </select>
      </div>

      {error && (
        <div style={{ color: "#ff6b6b", fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Server</th>
              <th style={th}>Patient</th>
              <th style={th}>Device ID</th>
              <th style={th}>Customer</th>
              <th style={th}>Duration</th>
              <th style={th}>Status</th>
              <th style={th}>Assigned To</th>
            </tr>
          </thead>
          <tbody>
            {!loading && patients.length === 0 && (
              <tr>
                <td style={td} colSpan={7}>
                  No assigned patients match this filter.
                </td>
              </tr>
            )}
            {patients.map((p) => (
              <tr key={`${p.server}:${p.patientId}`}>
                <td style={td}>
                  <span style={pill}>{p.server || "—"}</span>
                </td>
                <td style={td}>{p.name || "—"}</td>
                <td style={td}>{p.device_id}</td>
                <td style={td}>{p.customerName || "—"}</td>
                <td style={td}>
                  {p.exp_device_use_days ? `${p.exp_device_use_days}d` : "—"}
                </td>
                <td style={td}>{p.report_status_label}</td>
                <td style={td}>
                  {(p.assignedTo || []).length === 0 ? (
                    "—"
                  ) : (
                    p.assignedTo.map((a) => (
                      <span key={a.verifierId} style={pill}>
                        {a.verifierName}
                        {(a.blocks || []).map((b, i) => (
                          <span key={i} style={blockChip}>
                            {b}
                          </span>
                        ))}
                      </span>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Standalone page (still used by other pages' sidebars that link to
// /patient-assignments) — just the panel inside the usual page chrome.
export default function PatientAssignments() {
  return (
    <div style={pageWrap}>
      <SideBar
        menus={[
          { title: "Patient List",       url: "/patient-list" },
          { title: "Verifier Home",      url: "/verifier-home" },
          { title: "Analysis Dashboard", url: "/analysis-dashboard" },
          { title: "Users by Role",      url: "/users-by-role" },
          { title: "Telemetry",          url: "/telemetry" },
          { title: "Assign Patients",    url: "/assign-patients" },
          { title: "Verified",           url: "/Verification-Completed" },
          { title: "QC",                 url: "/qc_home" },
        ]}
        profileLink="verifier-profile"
      />
      <PatientAssignmentsPanel />
    </div>
  );
}
